// Backward-from-cursor extraction of the dotted identifier base
// (`t.`, `s.t.`, `[t].`, `` `t`. ``, `"t".`). The completion orchestrator
// only consults this when the cursor sits in code (not inside a string or
// comment) and within the bounds of the enclosing statement — both checks
// happen in `deriveContext.ts` before this module is invoked.

import type { DottedBase } from '../../../core/engines';

import { isInsideBlockComment, isInsideLineComment } from '../../sql-grammar/document/editorContextScan';
import { skipWhitespaceAndCommentsBackward } from './skipCommentsBackward';
import { isBareIdentChar } from './sourceScan';

/**
 * Walk back from `cursor` looking for `<ident>.<ident>.…` pattern. Returns
 * `undefined` when there's no `.` on the path. The in-progress identifier
 * being typed at the cursor is intentionally NOT included in `parts` —
 * `t.col|` produces base `[t]` so the resolver can use the rest of the
 * cursor token as a filter prefix.
 */
export function extractDottedBase(
  source: string,
  cursor: number,
  statementStart: number,
): DottedBase | undefined {
  let pos = cursor;
  while (pos > statementStart && isBareIdentChar(source.charCodeAt(pos - 1))) {
    pos -= 1;
  }
  // The in-progress identifier may be DELIMITED (`FROM [Sales].[▮`,
  // `"public"."▮`, `` `sakila`.`▮ ``): its opening quote sits between the dot
  // and the cursor, so the bare-char walk above stops on the quote and the dot
  // is never reached — the caret classified as `unknown` and answered with the
  // statement-start flood instead of the schema's objects. Step over ONE
  // opening delimiter so the dot scan below sees the base. Cross-engine on
  // purpose: `[` is T-SQL/SQLite, `"` PG/Oracle/SQLite, `` ` `` MySQL/SQLite —
  // and a `"`/`` ` `` that opens a STRING in a dialect never reaches this
  // function (deriveContext's string scan short-circuits first).
  if (pos > statementStart && isIdentifierOpenQuote(source.charCodeAt(pos - 1))) {
    pos -= 1;
  }

  const parts: string[] = [];
  const quoted: boolean[] = [];

  for (;;) {
    // Skip whitespace AND trailing block comments — `t./* c */col` and
    // `:NEW./* caret */col` are valid SQL where the comment is whitespace-
    // equivalent, so the dotted-access base must still resolve when the caret
    // sits just past such a comment.
    const dotPos = skipWhitespaceAndCommentsBackward(source, pos, statementStart);
    if (dotPos === statementStart || source.charCodeAt(dotPos - 1) !== 0x2e /* . */) break;

    // The `.` may be the terminal period of a preceding line/block comment
    // (e.g. `-- code follows BEGIN.` on the line above an empty body slot).
    // A comment-interior dot is never a real dotted-access base — treating it
    // as one mis-classifies the slot as `dotted-access` and suppresses the
    // statement-start keyword/table candidates. Skip it.
    const dotOffset = dotPos - 1;
    if (isInsideLineComment(source, dotOffset) || isInsideBlockComment(source, dotOffset)) break;

    const idEnd = skipWhitespaceAndCommentsBackward(source, dotPos - 1, statementStart);
    const ident = consumeIdentifierBackward(source, idEnd, statementStart);
    if (!ident) break;
    parts.unshift(ident.content);
    quoted.unshift(ident.quoted);
    pos = ident.start;
  }

  if (parts.length === 0) return undefined;
  return { parts, quoted };
}

/** `[`, `"` or `` ` `` — the character that OPENS a delimited identifier in at
 *  least one supported dialect. */
function isIdentifierOpenQuote(code: number): boolean {
  return code === 0x5b /* [ */ || code === 0x22 /* " */ || code === 0x60 /* ` */;
}

type IdentifierMatch = { readonly start: number; readonly content: string; readonly quoted: boolean };

function consumeIdentifierBackward(
  source: string,
  end: number,
  limit: number,
): IdentifierMatch | null {
  if (end <= limit) return null;
  const last = source.charCodeAt(end - 1);

  if (last === 0x5d /* ] */) {
    return matchClosedQuoteBackward(source, end, limit, 0x5b, 0x5d, /\]\]/g, ']');
  }
  if (last === 0x22 /* " */) {
    return matchClosedQuoteBackward(source, end, limit, 0x22, 0x22, /""/g, '"');
  }
  if (last === 0x60 /* ` */) {
    return matchClosedQuoteBackward(source, end, limit, 0x60, 0x60, /``/g, '`');
  }

  let start = end;
  while (start > limit && isBareIdentChar(source.charCodeAt(start - 1))) start -= 1;
  if (start === end) return null;
  return { start, content: source.slice(start, end), quoted: false };
}

function matchClosedQuoteBackward(
  source: string,
  end: number,
  limit: number,
  openCode: number,
  closeCode: number,
  doubledEscape: RegExp,
  unescapedTo: string,
): IdentifierMatch | null {
  let i = end - 2;
  while (i >= limit) {
    if (source.charCodeAt(i) === openCode) {
      // Symmetric quotes (`"`, `` ` ``) use doubled-escape; bracket pairs do not.
      if (openCode === closeCode && i > limit && source.charCodeAt(i - 1) === closeCode) {
        i -= 2;
        continue;
      }
      const inner = source.slice(i + 1, end - 1).replace(doubledEscape, unescapedTo);
      return { start: i, content: inner, quoted: true };
    }
    i -= 1;
  }
  return null;
}
